// Application constants
export const APP_CONFIG = {
  name: 'Transport Business Portal',
  version: '1.0.0',
  description: 'Comprehensive transport booking and fleet management system',
  author: 'Transport Solutions Ltd',
  supportEmail: 'support@transportsolutions.com',
};

// API Configuration
export const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3011/api',
  wsURL: process.env.REACT_APP_WS_URL || 'ws://localhost:3001',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  defaultTenantId: process.env.REACT_APP_DEFAULT_TENANT_ID || 'demo-tenant',
};

// Authentication
export const AUTH_CONFIG = {
  tokenKey: process.env.REACT_APP_ACCESS_TOKEN_KEY || 'transport_access_token',
  refreshTokenKey: process.env.REACT_APP_REFRESH_TOKEN_KEY || 'transport_refresh_token',
  sessionTimeout: parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '3600000'), // 1 hour
  idleTimeout: parseInt(process.env.REACT_APP_IDLE_TIMEOUT || '1800000'), // 30 minutes
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  DISPATCHER: 'dispatcher',
  FINANCE: 'finance',
  VIEWER: 'viewer',
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// Booking Statuses
export const BOOKING_STATUSES = {
  PENDING: 'pending',
  AWAITING_PAYMENT: 'awaiting_payment',
  CONFIRMED: 'confirmed',
  ASSIGNED: 'assigned',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
  FAILED: 'failed',
} as const;

export type BookingStatus = typeof BOOKING_STATUSES[keyof typeof BOOKING_STATUSES];

// Booking Types
export const BOOKING_TYPES = {
  TRANSFER: 'transfer',
  HOURLY: 'hourly',
} as const;

export type BookingType = typeof BOOKING_TYPES[keyof typeof BOOKING_TYPES];

// Driver Statuses - UPDATED to match API documentation
export const DRIVER_STATUSES = {
  AVAILABLE: 'available',
  BUSY: 'busy',
  OFFLINE: 'offline',
  BREAK: 'break',
} as const;

export type DriverStatus = typeof DRIVER_STATUSES[keyof typeof DRIVER_STATUSES];

// Vehicle Statuses - UPDATED to match API documentation
export const VEHICLE_STATUSES = {
  ACTIVE: 'active',
  MAINTENANCE: 'maintenance',
  RETIRED: 'retired',
} as const;

export type VehicleStatus = typeof VEHICLE_STATUSES[keyof typeof VEHICLE_STATUSES];

// Provider Statuses - UPDATED to match API documentation
export const PROVIDER_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

export type ProviderStatus = typeof PROVIDER_STATUSES[keyof typeof PROVIDER_STATUSES];

// Vehicle Classes
export const VEHICLE_CLASSES = {
  ECONOMY: 'economy',
  STANDARD: 'standard',
  PREMIUM: 'premium',
  VAN: 'van',
  MINIBUS: 'minibus',
  LUXURY: 'luxury',
} as const;

export type VehicleClass = typeof VEHICLE_CLASSES[keyof typeof VEHICLE_CLASSES];

// Payment Providers
export const PAYMENT_PROVIDERS = {
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
  CASH: 'cash',
  GPAY: 'gpay',
} as const;

export type PaymentProvider = typeof PAYMENT_PROVIDERS[keyof typeof PAYMENT_PROVIDERS];

// Payment Statuses
export const PAYMENT_STATUSES = {
  REQUIRES_ACTION: 'requires_action',
  SUCCEEDED: 'succeeded',
  PENDING: 'pending',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  PARTIAL_REFUND: 'partial_refund',
} as const;

export type PaymentStatus = typeof PAYMENT_STATUSES[keyof typeof PAYMENT_STATUSES];

// Assignment Statuses
export const ASSIGNMENT_STATUSES = {
  UNASSIGNED: 'unassigned',
  ASSIGNED: 'assigned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export type AssignmentStatus = typeof ASSIGNMENT_STATUSES[keyof typeof ASSIGNMENT_STATUSES];

// Notification Channels
export const NOTIFICATION_CHANNELS = {
  EMAIL: 'email',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
} as const;

export type NotificationChannel = typeof NOTIFICATION_CHANNELS[keyof typeof NOTIFICATION_CHANNELS];

// Notification Statuses
export const NOTIFICATION_STATUSES = {
  QUEUED: 'queued',
  SENT: 'sent',
  DELIVERED: 'delivered',
  FAILED: 'failed',
} as const;

export type NotificationStatus = typeof NOTIFICATION_STATUSES[keyof typeof NOTIFICATION_STATUSES];

// Pagination
export const PAGINATION = {
  defaultPageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGE_SIZE || '20'),
  maxPageSize: parseInt(process.env.REACT_APP_MAX_PAGE_SIZE || '100'),
  pageSizeOptions: [10, 20, 50, 100],
};

// Date formats
export const DATE_FORMATS = {
  display: 'MMM dd, yyyy',
  displayWithTime: 'MMM dd, yyyy HH:mm',
  input: 'yyyy-MM-dd',
  inputWithTime: "yyyy-MM-dd'T'HH:mm",
  iso: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  time: 'HH:mm',
  shortDate: 'MM/dd/yyyy',
  longDate: 'EEEE, MMMM do, yyyy',
};

// Currency
export const CURRENCY = {
  default: 'USD',
  supported: ['EUR', 'USD', 'GBP', 'LKR'],
  decimalPlaces: 2,
};

// File upload
export const FILE_UPLOAD = {
  maxSize: parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '5242880'), // 5MB
  allowedTypes: (process.env.REACT_APP_ALLOWED_FILE_TYPES || 'image/jpeg,image/png,image/webp,application/pdf').split(','),
  acceptedImageFormats: ['image/jpeg', 'image/png', 'image/webp'],
  acceptedDocumentFormats: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};

// WebSocket Events
export const WEBSOCKET_EVENTS = {
  // Connection
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',

  // Dashboard
  DASHBOARD_UPDATE: 'dashboard:update',

  // Bookings
  BOOKING_CREATED: 'booking:created',
  BOOKING_UPDATED: 'booking:updated',
  BOOKING_CANCELLED: 'booking:cancelled',

  // Assignments
  ASSIGNMENT_CREATED: 'assignment:created',
  ASSIGNMENT_UPDATED: 'assignment:updated',

  // Drivers
  DRIVER_STATUS_CHANGED: 'driver:status_changed',
  DRIVER_LOCATION_UPDATE: 'driver:location_update',

  // Payments
  PAYMENT_PROCESSED: 'payment:processed',
  PAYMENT_FAILED: 'payment:failed',

  // Notifications
  NOTIFICATION_SENT: 'notification:sent',
} as const;

// Status colors for UI
// Booking status colors
export const BOOKING_STATUS_COLORS = {
  [BOOKING_STATUSES.PENDING]: 'warning',
  [BOOKING_STATUSES.AWAITING_PAYMENT]: 'warning',
  [BOOKING_STATUSES.CONFIRMED]: 'success',
  [BOOKING_STATUSES.ASSIGNED]: 'info',
  [BOOKING_STATUSES.COMPLETED]: 'success',
  [BOOKING_STATUSES.CANCELLED]: 'default',
  [BOOKING_STATUSES.REFUNDED]: 'default',
  [BOOKING_STATUSES.FAILED]: 'error',
} as const;

// Driver status colors - UPDATED
export const DRIVER_STATUS_COLORS = {
  [DRIVER_STATUSES.AVAILABLE]: 'success',
  [DRIVER_STATUSES.BUSY]: 'info',
  [DRIVER_STATUSES.OFFLINE]: 'error',
  [DRIVER_STATUSES.BREAK]: 'warning',
} as const;

// Vehicle status colors - UPDATED
export const VEHICLE_STATUS_COLORS = {
  [VEHICLE_STATUSES.ACTIVE]: 'success',
  [VEHICLE_STATUSES.MAINTENANCE]: 'warning',
  [VEHICLE_STATUSES.RETIRED]: 'error',
} as const;

// Provider status colors - UPDATED
export const PROVIDER_STATUS_COLORS = {
  [PROVIDER_STATUSES.ACTIVE]: 'success',
  [PROVIDER_STATUSES.INACTIVE]: 'error',
  [PROVIDER_STATUSES.SUSPENDED]: 'error',
} as const;

// Payment status colors
export const PAYMENT_STATUS_COLORS = {
  [PAYMENT_STATUSES.SUCCEEDED]: 'success',
  [PAYMENT_STATUSES.PENDING]: 'warning',
  [PAYMENT_STATUSES.FAILED]: 'error',
  [PAYMENT_STATUSES.REFUNDED]: 'default',
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: AUTH_CONFIG.tokenKey,
  REFRESH_TOKEN: AUTH_CONFIG.refreshTokenKey,
  USER_PREFERENCES: 'transport_user_preferences',
  SIDEBAR_COLLAPSED: 'transport_sidebar_collapsed',
  THEME: 'transport_theme',
  LANGUAGE: 'transport_language',
  LAST_ROUTE: 'transport_last_route',
};

// Chart colors
export const CHART_COLORS = {
  primary: '#3b82f6',
  secondary: '#64748b',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#06b6d4',
  purple: '#8b5cf6',
  pink: '#ec4899',
  indigo: '#6366f1',
  teal: '#14b8a6',
  orange: '#f97316',
  gray: '#6b7280',
};

// Breakpoints (matching Tailwind)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

// Animation durations
export const ANIMATION_DURATION = {
  fast: 150,
  normal: 300,
  slow: 500,
  verySlow: 1000,
};

// Z-index values
export const Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  toast: 1080,
};

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. Please contact your administrator.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

// Success messages
export const SUCCESS_MESSAGES = {
  SAVE_SUCCESS: 'Changes saved successfully.',
  DELETE_SUCCESS: 'Item deleted successfully.',
  UPDATE_SUCCESS: 'Item updated successfully.',
  CREATE_SUCCESS: 'Item created successfully.',
  SEND_SUCCESS: 'Message sent successfully.',
  UPLOAD_SUCCESS: 'File uploaded successfully.',
};

// Feature flags
export const FEATURES = {
  ENABLE_WEBSOCKET: process.env.REACT_APP_ENABLE_WEBSOCKET === 'true',
  ENABLE_NOTIFICATIONS: process.env.REACT_APP_ENABLE_NOTIFICATIONS === 'true',
  ENABLE_ANALYTICS: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
  ENABLE_DEBUG: process.env.REACT_APP_ENABLE_DEBUG === 'true',
  MOCK_API: process.env.REACT_APP_MOCK_API === 'true',
};

// Environment
export const ENVIRONMENT = {
  isDevelopment: process.env.NODE_ENV === 'development',
  isProduction: process.env.NODE_ENV === 'production',
  isTest: process.env.NODE_ENV === 'test',
};